/**
 * useImportEltJobs Hook
 *
 * React Query mutation hook for importing ELT jobs.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { eltJobService } from '../../../api/elt-job.service';
import { ELT_JOBS_QUERY_KEY } from './useEltJobs';
import type { EltJobCreateDTO, EltJobImportResponse } from '../../../types/elt-job.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UseImportEltJobsOptions {
  onSuccess?: (data: EltJobImportResponse) => void;
  onError?: (error: string) => void;
}

export function useImportEltJobs(tenantId: string, options?: UseImportEltJobsOptions) {
  const queryClient = useQueryClient();

  return useMutation<EltJobImportResponse, Error, EltJobCreateDTO[]>({
    mutationFn: (jobs: EltJobCreateDTO[]) =>
      eltJobService.importEltJobs(tenantId, jobs),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [ELT_JOBS_QUERY_KEY, tenantId] });
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(getErrorMessage(error));
    },
  });
}
